import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, Modal, Label, Input } from "reactstrap";
import MetaTags from "react-meta-tags";
import Datatables from "pages/table/datatable";
import { tableColumns } from "./components/TableColumns";
import handleExportCSV from "pages/utils/handleExportCSV";
import StatsCard from "./components/StatsCard";
import FilterBar from "./components/FilterBar";
import InvoicePreview from "pages/utils/InvoicePreview";
import { showSuccessAlert, showErrorAlert } from "pages/utils/Alerts/alertMessages";
import { get, post } from "helpers/api_helper";

const Subscriptions = () => {
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [viewingInvoice, setViewingInvoice] = useState(null);
    const [subsList, setSubsList] = useState([]);
    const [activeTab, setActiveTab] = useState("1");
    const [plansList, setPlansList] = useState([]);
    const [editPlanModal, setEditPlanModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [planForm, setPlanForm] = useState({
        id: "", plan_name: "", billing_interval: "monthly", price: 0,
        trial_period_days: 0, automatic_renewal: true, retry_count: 3, failed_payment_action: "suspend", description: ""
    });

    const fetchSubs = useCallback(async () => {
        try {
            const res = await get("/superadmin/subscriptions");
            const list = res?.data || res;
            if (Array.isArray(list)) {
                const formatted = list.map((s, idx) => ({
                    id: s.subscription_id || idx + 1,
                    company_name: s.Entity?.entity_title || "Company #" + (s.entity_id || idx + 1),
                    plan_name: s.SubscriptionPlan?.plan_type || "Starter Plan",
                    price: s.SubscriptionPlan?.plan_price ? `$${s.SubscriptionPlan.plan_price}` : "$49",
                    billing_cycle: s.SubscriptionPlan?.billing_interval || "Monthly",
                    status: s.status === 0 ? "Active" : "Expired",
                    start_date: s.subscription_start ? new Date(s.subscription_start).toLocaleDateString() : "—",
                    next_billing: s.subscription_end ? new Date(s.subscription_end).toLocaleDateString() : "—",
                }));
                setSubsList(formatted);
            }
        } catch (e) {
            console.error("Failed to load subscriptions from database", e);
        }
    }, []);

    const fetchPlans = useCallback(async () => {
        try {
            const res = await get("/superadmin/subscription-plans");
            const data = res?.data || res;
            if (Array.isArray(data)) {
                setPlansList(data);
            }
        } catch (e) {
            console.error("Failed to load subscription plans from database", e);
        }
    }, []);

    useEffect(() => {
        fetchSubs();
        fetchPlans();
    }, [fetchSubs, fetchPlans]);

    const handleAction = (type, row) => {
        if (type === "View") {
            setViewingInvoice(row);
        }
    };

    const toggleModal = () => {
        setViewingInvoice(null);
    };

    const handleEditPlan = (plan) => {
        setSelectedPlan(plan);
        setPlanForm({
            id: plan.id,
            plan_name: plan.plan_name,
            billing_interval: plan.billing_interval || "monthly",
            price: plan.price || 0,
            trial_period_days: plan.trial_period_days || 0,
            automatic_renewal: plan.automatic_renewal !== undefined ? plan.automatic_renewal : true,
            retry_count: plan.retry_count || 3,
            failed_payment_action: plan.failed_payment_action || "suspend",
            description: plan.description || ""
        });
        setEditPlanModal(true);
    };

    const handlePlanFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPlanForm(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handlePlanSubmit = async () => {
        try {
            await post("/superadmin/subscription-plans", planForm);
            showSuccessAlert("Subscription plan saved to database successfully!");
            fetchPlans();
            setEditPlanModal(false);
        } catch (e) {
            showErrorAlert("Failed to save plan to database.");
        }
    };

    return (
        <React.Fragment>
            <div className="dash-page ms-4 me-4">
                <MetaTags>
                    <title>Subscriptions | {process.env.REACT_APP_TITLE}</title>
                </MetaTags>

                <Container fluid>
                    <div className="p-3">
                        <Row className="align-items-center mb-4">
                            <Col md="6">
                                <h4 className="mb-1 fw-bold text-dark">Subscription Management</h4>
                                <p className="text-muted mb-0">Manage active subscriber contracts and billing cycle intervals</p>
                            </Col>
                            <Col md="6" className="d-flex justify-content-end gap-2">
                                <button className="btn btn-success border px-3" onClick={handleExportCSV}>
                                    <i className="bx bx-export me-1"></i> Export Active List ({selectedRowIds.length})
                                </button>
                            </Col>
                        </Row>

                        <Nav tabs className="nav-tabs-custom nav-justified mb-4">
                            <NavItem>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={activeTab === "1" ? "active" : ""}
                                    onClick={() => setActiveTab("1")}
                                >
                                    <i className="bx bx-list-ul me-1" /> Active Subscriptions
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={activeTab === "2" ? "active" : ""}
                                    onClick={() => setActiveTab("2")}
                                >
                                    <i className="bx bx-slider-alt me-1" /> Pricing Plans & Retry Rules
                                </NavLink>
                            </NavItem>
                        </Nav>

                        <TabContent activeTab={activeTab}>
                            <TabPane tabId="1">
                                <StatsCard subsList={subsList} />
                                <FilterBar />
                                <div className="p-3 bg-white shadow-sm border" style={{ borderRadius: "10px" }}>
                                    <Datatables
                                        columns={tableColumns(handleAction)}
                                        showTableOnly={true}
                                        rows={subsList}
                                        keyField={"id"}
                                        isCheckbox={true}
                                        loading={false}
                                        setSelectedRowIds={setSelectedRowIds}
                                    />
                                </div>
                            </TabPane>

                            <TabPane tabId="2">
                                <Row className="g-4">
                                    {plansList.map(plan => (
                                        <Col lg="4" md="6" key={plan.id}>
                                            <Card className="h-100 shadow-sm border border-light" style={{ borderRadius: "12px" }}>
                                                <CardBody className="p-4 d-flex flex-column justify-content-between">
                                                    <div>
                                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                                            <h5 className="fw-bold text-dark mb-0">{plan.plan_name}</h5>
                                                            <span className="badge bg-primary-subtle text-primary text-uppercase small">{plan.billing_interval}</span>
                                                        </div>
                                                        <h3 className="fw-bolder text-primary mb-3">
                                                            ${plan.price} <span className="small text-muted fw-normal" style={{ fontSize: "14px" }}>/ cycle</span>
                                                        </h3>
                                                        <p className="text-muted small mb-4">{plan.description}</p>

                                                        <hr />

                                                        <ul className="list-unstyled mb-4">
                                                            <li className="mb-2 small d-flex align-items-center justify-content-between">
                                                                <span className="text-muted">Free Trial Days</span>
                                                                <span className="fw-semibold text-dark">{plan.trial_period_days} Days</span>
                                                            </li>
                                                            <li className="mb-2 small d-flex align-items-center justify-content-between">
                                                                <span className="text-muted">Auto-Renewals</span>
                                                                <span className={`fw-semibold ${plan.automatic_renewal ? "text-success" : "text-danger"}`}>
                                                                    {plan.automatic_renewal ? "Enabled" : "Disabled"}
                                                                </span>
                                                            </li>
                                                            <li className="mb-2 small d-flex align-items-center justify-content-between">
                                                                <span className="text-muted">Retry Strategy</span>
                                                                <span className="fw-semibold text-dark">Retry {plan.retry_count} times</span>
                                                            </li>
                                                            <li className="mb-2 small d-flex align-items-center justify-content-between">
                                                                <span className="text-muted">Failure Action</span>
                                                                <span className="badge bg-danger-subtle text-danger text-uppercase small">{plan.failed_payment_action}</span>
                                                            </li>
                                                        </ul>
                                                    </div>

                                                    <button
                                                        className="btn btn-outline-primary w-100 py-2 fw-semibold"
                                                        style={{ border: "1px solid #243E79", color: "#243E79" }}
                                                        onClick={() => handleEditPlan(plan)}
                                                    >
                                                        <i className="bx bx-cog me-1" /> Configure Plan Parameters
                                                    </button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </TabPane>
                        </TabContent>
                    </div>

                    {viewingInvoice && (
                        <InvoicePreview
                            isOpen={!!viewingInvoice}
                            toggle={toggleModal}
                            data={viewingInvoice}
                        />
                    )}

                    {/* Edit Plan Settings Modal */}
                    <Modal isOpen={editPlanModal} toggle={() => setEditPlanModal(false)} size="lg" className="custom-modal">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold">Configure Plan: {planForm.plan_name}</h5>
                            <button type="button" onClick={() => setEditPlanModal(false)} className="close border-0 bg-transparent" aria-label="Close">
                                <i className="bx bx-x h3 m-0" />
                            </button>
                        </div>

                        <div className="modal-body p-4">
                            <Row className="g-3">
                                <Col md="6">
                                    <Label className="fw-semibold small">Plan Title</Label>
                                    <Input type="text" name="plan_name" className="py-2" value={planForm.plan_name} onChange={handlePlanFormChange} />
                                </Col>
                                <Col md="6">
                                    <Label className="fw-semibold small">Billing Cycle Interval</Label>
                                    <select name="billing_interval" className="form-select py-2" value={planForm.billing_interval} onChange={handlePlanFormChange}>
                                        <option value="monthly">Monthly</option>
                                        <option value="quarterly">Quarterly</option>
                                        <option value="half-yearly">Half-Yearly</option>
                                        <option value="annual">Annual</option>
                                    </select>
                                </Col>
                                <Col md="6">
                                    <Label className="fw-semibold small">Price ($)</Label>
                                    <Input type="number" name="price" className="py-2" value={planForm.price} onChange={handlePlanFormChange} />
                                </Col>
                                <Col md="6">
                                    <Label className="fw-semibold small">Trial Period Duration (Days)</Label>
                                    <Input type="number" name="trial_period_days" className="py-2" value={planForm.trial_period_days} onChange={handlePlanFormChange} />
                                </Col>
                                <Col md="6">
                                    <Label className="fw-semibold small">Max Auto-Charge Retry Count</Label>
                                    <Input type="number" name="retry_count" className="py-2" value={planForm.retry_count} onChange={handlePlanFormChange} />
                                </Col>
                                <Col md="6">
                                    <Label className="fw-semibold small">Failed Payment Handler Action</Label>
                                    <select name="failed_payment_action" className="form-select py-2" value={planForm.failed_payment_action} onChange={handlePlanFormChange}>
                                        <option value="suspend">Suspend User Workspace</option>
                                        <option value="cancel">Cancel Plan Registration</option>
                                        <option value="retry">Indefinite retry hold</option>
                                    </select>
                                </Col>
                                <Col md="12">
                                    <Label className="fw-semibold small">Plan Description</Label>
                                    <Input type="textarea" rows="3" name="description" value={planForm.description} onChange={handlePlanFormChange} />
                                </Col>
                                <Col md="12" className="mt-3">
                                    <Label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                                        <Input type="checkbox" name="automatic_renewal" checked={planForm.automatic_renewal} onChange={handlePlanFormChange} />
                                        <span className="fw-semibold small">Enable Automatic Renewal Charges</span>
                                    </Label>
                                </Col>
                            </Row>

                            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                <button className="btn btn-light" style={{ width: "100px" }} onClick={() => setEditPlanModal(false)}>Cancel</button>
                                <button className="btn btn-primary px-4" onClick={handlePlanSubmit} style={{ background: "#243E79", borderColor: "#243E79", width: "140px" }}>
                                    Save Configs
                                </button>
                            </div>
                        </div>
                    </Modal>

                </Container>
            </div>
        </React.Fragment>
    );
};

export default Subscriptions;
